import React, { useEffect, useMemo, useRef, useState } from 'react'
import { TimeSeriesChart } from '../TimeSeriesChart'
import type { TimeSeriesSeries } from '../TimeSeriesChart/types'
import { historyRowsToSeries, type HistoryChartRow } from './chart/historyRowsToSeries'
import { parseTelemetryTimeMs } from './chart/parseTelemetryTime'
import * as s from './ChartView.css'

export type ChartViewRow = HistoryChartRow & { id: string }

/**
 * Range end at/after (now − this window), or in the future (e.g. "Today" → tomorrow
 * 12AM) → treat as live and append WS points into the open right side of the chart.
 */
const LIVE_TRAILING_MS = 2 * 60 * 60 * 1000
const EMPTY_LIVE_ROWS: ChartViewRow[] = []

export interface ChartViewProps<TRow extends ChartViewRow = ChartViewRow> {
  rows: TRow[]
  loading?: boolean
  fetching?: boolean
  fillContainer?: boolean
  emptyTitle?: string
  emptySubtitle?: string
  height?: number
  /** ISO start/end for the selected ListView date range — pins the X-axis. */
  timeRangeStart?: string | null
  timeRangeEnd?: string | null
  /**
   * Real-time telemetry flattened into chart rows. Merged with `rows` when the
   * selected range is live (end near now, or end in the future for calendar
   * presets like "Today"). Only keys already present in `rows` are appended
   * so the Parameters filter stays authoritative.
   */
  liveRows?: ChartViewRow[]
  /** 12-hour clock with AM/PM on the time axis (default true). */
  hour12?: boolean
}

function isLiveTrailingRange(endISO: string | null | undefined, now = Date.now()): boolean {
  if (!endISO) return false
  const end = Date.parse(endISO)
  if (!Number.isFinite(end)) return false
  // Includes future ends (Today → tomorrow midnight) so live points keep filling in.
  return end >= now - LIVE_TRAILING_MS
}

function mergeChartRows(base: ChartViewRow[], live: ChartViewRow[]): ChartViewRow[] {
  if (live.length === 0) return base
  const activeKeys = new Set(base.map((r) => r.key))
  const byId = new Map<string, ChartViewRow>()
  for (const r of base) byId.set(r.id, r)
  for (const r of live) {
    // If we already have historical rows, only append keys the user is charting
    if (activeKeys.size > 0 && !activeKeys.has(r.key)) continue
    byId.set(r.id, r)
  }
  return [...byId.values()]
}

function resolveLiveXDomain(
  timeRangeStart: string | null | undefined,
  timeRangeEnd: string | null | undefined,
  liveRows: ChartViewRow[],
  now = Date.now(),
): { min: number; max: number } | null {
  const min = timeRangeStart ? Date.parse(timeRangeStart) : NaN
  const max = timeRangeEnd ? Date.parse(timeRangeEnd) : NaN
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null

  // Fixed domain when the selected end is in the future (e.g. "Today" → tomorrow 12AM).
  // Live points fill the open right side without shifting the axis.
  if (max > now || !isLiveTrailingRange(timeRangeEnd, now) || liveRows.length === 0) {
    return { min, max }
  }

  const duration = max - min
  let liveMax = Math.max(now, max)
  for (const r of liveRows) {
    const t = parseTelemetryTimeMs(r.time)
    if (t != null) liveMax = Math.max(liveMax, t)
  }
  return { min: liveMax - duration, max: liveMax }
}

export function ChartView<TRow extends ChartViewRow>({
  rows,
  loading = false,
  fetching = false,
  fillContainer = false,
  emptyTitle = 'No data to chart',
  emptySubtitle,
  height = 360,
  timeRangeStart = null,
  timeRangeEnd = null,
  liveRows = [],
  hour12 = true,
}: ChartViewProps<TRow>) {
  const liveEnabled = isLiveTrailingRange(timeRangeEnd)
  const effectiveLive = liveEnabled && liveRows.length > 0 ? liveRows : EMPTY_LIVE_ROWS

  const mergedRows = useMemo(
    () => mergeChartRows(rows, effectiveLive),
    [rows, effectiveLive],
  )

  const baseSeries = useMemo(() => historyRowsToSeries(mergedRows), [mergedRows])

  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set())
  const [pulseGen, setPulseGen] = useState(0)
  const prevLiveLatestRef = useRef(0)

  useEffect(() => {
    const ids = new Set(baseSeries.map((s) => s.id))
    setHiddenIds((prev) => {
      let changed = false
      const next = new Set<string>()
      for (const id of prev) {
        if (ids.has(id)) next.add(id)
        else changed = true
      }
      return changed ? next : prev
    })
  }, [baseSeries])

  // Pulse when a newer live timestamp arrives
  useEffect(() => {
    if (effectiveLive.length === 0) return
    let latest = 0
    for (const r of effectiveLive) {
      const t = parseTelemetryTimeMs(r.time)
      if (t != null) latest = Math.max(latest, t)
    }
    if (latest > prevLiveLatestRef.current) {
      prevLiveLatestRef.current = latest
      setPulseGen((g) => g + 1)
    }
  }, [effectiveLive])

  const series: TimeSeriesSeries[] = useMemo(
    () =>
      baseSeries.map((s) => ({
        ...s,
        visible: !hiddenIds.has(s.id),
      })),
    [baseSeries, hiddenIds],
  )

  const xDomain = useMemo(
    () => resolveLiveXDomain(timeRangeStart, timeRangeEnd, effectiveLive),
    [timeRangeStart, timeRangeEnd, effectiveLive],
  )

  const handleLegendToggle = (id: string, visible: boolean) => {
    setHiddenIds((prev) => {
      const next = new Set(prev)
      if (visible) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const showLoading = loading && rows.length === 0
  const emptyMessage = emptySubtitle
    ? `${emptyTitle}. ${emptySubtitle}`
    : emptyTitle

  return (
    <div className={fillContainer ? s.chartWrapFill : s.chartWrap}>
      {fetching && !showLoading && (
        <div className={s.fetchingBar} aria-hidden="true" />
      )}
      {pulseGen > 0 && (
        <div key={pulseGen} className={s.livePulse} aria-hidden="true" />
      )}
      <TimeSeriesChart
        series={series}
        loading={showLoading}
        emptyMessage={emptyMessage}
        onLegendToggle={handleLegendToggle}
        height={fillContainer ? '100%' : height}
        className={fillContainer ? s.chartFill : undefined}
        xDomain={xDomain}
        hour12={hour12}
      />
    </div>
  )
}
